// frontend/src/pages/BulkPosting.js
//
// Copy source: docs/content/bulk-posting-page.md
//
// Page shape is deliberate. The SERP for "bulk social media posting" is mixed —
// guides (Sprinklr, MeetEdgar) and product pages (Cloud Campaign, Bulkly) both
// rank — so a pure sales page cannot win it. This teaches and sells: definition
// first (written for the featured snippet), then methods, then the spec.
//
// NOTE: plain <table className='table'> throughout, NOT react-bootstrap's
// <Table>. That component was part of the React 19 blank-screen crash fixed in
// cdab6060 and is deliberately not reintroduced here.
import '../custom.css'

import React from 'react'
import { Container, Row, Col, Image, Button, Card, Accordion } from 'react-bootstrap'
import { Upload, FileEarmarkZip, Stars, CheckCircleFill, ExclamationTriangleFill } from 'react-bootstrap-icons'

const heroImg  = '/assets/images/bulk/bulk-content-planner.webp'
const csvImg   = '/assets/images/bulk/bulk-csv-spreadsheet.webp'
// Real product screenshot, not stock — it shows where a bulk upload actually
// lands. Restored after being replaced with stock photography by mistake.
const calendarShot = '/assets/logo/calender.webp'
const TEMPLATE = '/assets/greatly-social-bulk-template.csv'

const NETWORKS = [
  ['Instagram', 'Image or video',  '2,200',      'Carousels up to 10 items. Reels 9:16.'],
  ['Facebook',  'Image or video',  '63,206',     'Longest caption limit of any network.'],
  ['Threads',   'Image or video',  '500',        ''],
  ['X',         'Image or video',  '280',        'Up to 4 images per post.'],
  ['LinkedIn',  'Image or video',  '3,000',      'Personal profile or company page.'],
  ['TikTok',    'Video only',      '2,200',      'A row with only an image skips TikTok.'],
  ['Pinterest', 'Image required',  '500',        'pinterest_board column required.'],
  ['YouTube',   'Video only',      '5,000',      'youtube_title required. Shorts ≤ 60s.'],
]

const REQUIRED_COLS = [
  ['date',     'YYYY-MM-DD',                                  '2026-09-14'],
  ['time',     'HH:MM (24-hour)',                             '09:30'],
  ['caption',  'Text',                                        'Autumn collection is live.'],
  ['media',    'URL or filename. Comma-separate for carousels.', 'https://…/hero.jpg'],
  ['networks', 'Comma-separated',                             'instagram,facebook,linkedin'],
]

const OPTIONAL_COLS = [
  ['timezone',            'IANA zone, e.g. America/New_York. Defaults to your account timezone.'],
  ['caption_instagram, caption_x, caption_linkedin …', 'Override the caption for one network. Blank falls back to caption.'],
  ['first_comment',       'Posted as the first comment — where hashtags belong on Instagram.'],
  ['alt_text',            'Image alt text. Good practice, and it helps accessibility.'],
  ['pinterest_board',     'Board name. Required if you target Pinterest.'],
  ['youtube_title',       'Required if you target YouTube.'],
]

const FAQS = [
  ['What file formats can I upload?',
   'CSV, or a ZIP containing your CSV plus media files. Start from our template so the column names match.'],
  ['How many posts can I schedule at once?',
   'As many as your monthly allowance covers — 15 on Starter, 30 on Pro, 100 on Agency. Larger batches are validated and queued in one pass.'],
  ['Can I write a different caption for each network?',
   'Yes. Add caption_instagram, caption_linkedin and so on. Blank columns fall back to the default caption.'],
  ['Do I need to host my images somewhere?',
   'No. Either link to publicly accessible URLs, or upload a ZIP with the files included.'],
  ['Which networks does bulk posting support?',
   'All eight: Instagram, Facebook, Threads, X, LinkedIn, TikTok, Pinterest and YouTube.'],
  ['What happens if a row is wrong?',
   'Validation runs before anything is scheduled and reports the row number and the problem. Nothing publishes until you confirm.'],
  ['Can I bulk schedule video?',
   'Yes — video works the same as images, including TikTok, Reels and YouTube Shorts.'],
  ['Is there a free trial?',
   '14 days, no credit card.'],
]

export default function BulkPosting () {
  return (
    <>
      {/* ───────── HERO ───────── */}
      <section className='full-bleed py-5 bg-primary-soft'>
        <Container fluid='lg'>
          <Row className='align-items-center g-5'>
            <Col md={6}>
              <h1 className='display-5 fw-bold mb-3'>Post to all social media at once</h1>
              <p className='lead mb-4'>
                Upload one spreadsheet. Fill a month of calendar across all eight
                networks in a single pass.
              </p>
              <Button variant='primary' size='lg' href='/register' className='me-2 mb-2'>
                Start free trial
              </Button>
              <Button variant='outline-primary' size='lg' href={TEMPLATE} className='mb-2'>
                Download the CSV template
              </Button>
            </Col>
            <Col md={6}>
              <Image
                src={heroImg}
                alt='A month of content being planned out on a paper content planner'
                fluid
                className='shadow-card rounded-4'
              />
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── DEFINITION (featured-snippet target) ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <p className='fs-5'>
            <strong>Bulk social media posting</strong> is scheduling many posts at once
            from a single file instead of creating them one at a time. You prepare your
            posts in a spreadsheet — caption, date, time, media and target networks on
            each row — upload it once, and every post is queued and published
            automatically at the time you set.
          </p>
          <p className='text-muted fs-5 mb-0'>
            For anyone posting more than a few times a week, it is the difference between
            an afternoon of copy-pasting and a five-minute upload.
          </p>
        </Container>
      </section>

      {/* ───────── THREE METHODS ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg'>
          <Row className='justify-content-center mb-4'>
            <Col lg={8} className='text-center'>
              <h2 className='fw-bold mb-3'>Three ways to fill a calendar in bulk</h2>
              <p className='text-muted fs-5 mb-0'>
                All three land in the same review screen before anything publishes.
              </p>
            </Col>
          </Row>
          <Row className='g-4'>
            {[
              { icon: <Upload />,          title: 'Upload a CSV',      body: 'Link to media hosted anywhere public — Drive, Dropbox, S3, your own site.' },
              { icon: <FileEarmarkZip />,  title: 'Upload a ZIP',      body: 'Drop your photos and videos in alongside the spreadsheet. No hosting needed.' },
              { icon: <Stars />,           title: 'Generate with AI',  body: 'Describe your business and get a batch of written posts back, ready to review.' },
            ].map(({ icon, title, body }, i) => (
              <Col md={4} key={i}>
                <Card className='h-100 shadow-card'>
                  <Card.Body>
                    <div className='fs-3 text-primary mb-2'>{icon}</div>
                    <Card.Title className='fw-semibold'>{title}</Card.Title>
                    <Card.Text className='text-muted'>{body}</Card.Text>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        </Container>
      </section>

      {/* ───────── BEFORE YOU START ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <Card className='shadow-card border-0' style={{ borderLeft: '4px solid #ffc107' }}>
            <Card.Body className='p-4'>
              <h2 className='fw-bold h4 mb-3 d-flex align-items-center gap-2'>
                <ExclamationTriangleFill className='text-warning' />
                Every row needs a photo or a video
              </h2>
              <p className='text-muted mb-3'>
                Greatly Social publishes media posts, not text-only updates. A row without
                media is rejected at validation, with the row number, so you can fix it and
                re-upload.
              </p>
              <p className='text-muted mb-0'>
                This is not an arbitrary limit. Instagram, Pinterest, TikTok and YouTube
                reject text-only posts outright, so a mixed batch would silently fail on
                half your networks. Requiring media on every row means a batch that
                validates is a batch that publishes.
              </p>
            </Card.Body>
          </Card>
        </Container>
      </section>

      {/* ───────── METHOD 1 — CSV ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg'>
          <Row className='align-items-center g-5'>
            <Col md={6}>
              <Image src={csvImg} alt='A spreadsheet of scheduled posts open on a laptop' fluid className='shadow-card rounded-4' />
            </Col>
            <Col md={6}>
              <p className='text-uppercase fw-bold text-primary small mb-2'>Method 1</p>
              <h2 className='fw-bold mb-3'>CSV with media links</h2>
              <p className='text-muted fs-5'>
                For when your images already live somewhere public — Dropbox, Drive, S3,
                your own site, a stock library.
              </p>
              <ol className='text-muted ps-3'>
                <li className='mb-2'>
                  <a href={TEMPLATE}>Download the template</a> — column names must match
                  exactly, so start here rather than building your own.
                </li>
                <li className='mb-2'>Fill one row per post: caption, date, time, media, networks.</li>
                <li className='mb-2'>Upload it in Studio → Bulk.</li>
                <li className='mb-2'>Review — every row validated and previewed per network.</li>
                <li>Confirm, and posts publish automatically at the times you set.</li>
              </ol>
              <p className='text-muted mb-0'>
                <strong>Media links must be publicly reachable.</strong> A Drive link set to
                &ldquo;restricted&rdquo; will fail validation. We fetch each file at upload
                time and re-host it, so your posts don&rsquo;t break if the original moves.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── METHOD 2 — ZIP ───────── */}
      <section className='py-5'>
        <Container style={{ maxWidth: 760 }}>
          <p className='text-uppercase fw-bold text-primary small mb-2'>Method 2</p>
          <h2 className='fw-bold mb-3'>ZIP upload</h2>
          <p className='text-muted fs-5'>
            For when the files are on your machine and you don&rsquo;t want to host them
            first. Put your CSV and your media in one ZIP, then reference files by
            filename instead of URL:
          </p>
          <pre className='bg-light border rounded-3 p-3 small mb-3'>{`media
launch-hero.jpg
reel-teaser.mp4
carousel-1.jpg,carousel-2.jpg,carousel-3.jpg`}</pre>
          <p className='text-muted mb-0'>
            Files are matched to rows by filename. Anything referenced but missing — or
            present but unreferenced — is flagged before you confirm.
          </p>
        </Container>
      </section>

      {/* ───────── CSV FORMAT ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg'>
          <h2 className='fw-bold mb-2'>The CSV format</h2>
          <p className='text-muted fs-5 mb-4'>One row = one post. Only five columns are required.</p>

          <h3 className='h5 fw-semibold mb-3'>Required</h3>
          <div className='table-responsive mb-5'>
            <table className='table table-bordered bg-white align-middle'>
              <thead className='table-light'>
                <tr><th>Column</th><th>Format</th><th>Example</th></tr>
              </thead>
              <tbody>
                {REQUIRED_COLS.map(([c, f, e]) => (
                  <tr key={c}>
                    <td><code>{c}</code></td>
                    <td className='text-muted'>{f}</td>
                    <td className='text-muted'><code>{e}</code></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className='text-muted'>
            Valid network values: <code>instagram</code> · <code>facebook</code> ·{' '}
            <code>threads</code> · <code>x</code> · <code>linkedin</code> ·{' '}
            <code>tiktok</code> · <code>pinterest</code> · <code>youtube</code>
          </p>

          <h3 className='h5 fw-semibold mb-3 mt-5'>Optional</h3>
          <div className='table-responsive mb-4'>
            <table className='table table-bordered bg-white align-middle'>
              <thead className='table-light'>
                <tr><th>Column</th><th>What it does</th></tr>
              </thead>
              <tbody>
                {OPTIONAL_COLS.map(([c, d]) => (
                  <tr key={c}><td><code>{c}</code></td><td className='text-muted'>{d}</td></tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className='text-muted mb-0'>
            <strong>The per-network caption override is the column that earns its keep.</strong>{' '}
            The same post rarely works on LinkedIn and TikTok. Write the default once,
            override where it matters, leave the rest blank.
          </p>
        </Container>
      </section>

      {/* ───────── NETWORK REQUIREMENTS ───────── */}
      <section className='py-5'>
        <Container fluid='lg'>
          <h2 className='fw-bold mb-2'>Network requirements</h2>
          <p className='text-muted fs-5 mb-4'>
            What each network needs from a row, so a batch that validates is a batch that
            publishes.
          </p>
          <div className='table-responsive mb-4'>
            <table className='table table-bordered align-middle'>
              <thead className='table-light'>
                <tr><th>Network</th><th>Media</th><th>Caption limit</th><th>Notes</th></tr>
              </thead>
              <tbody>
                {NETWORKS.map(([n, m, c, note]) => (
                  <tr key={n}>
                    <td className='fw-semibold'>{n}</td>
                    <td className='text-muted'>{m}</td>
                    <td className='text-muted'>{c}</td>
                    <td className='text-muted small'>{note}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <p className='text-muted mb-0'>
            <strong>Rows are never silently dropped.</strong> If a row targets TikTok with
            only a photo, validation tells you before you confirm — it doesn&rsquo;t fail
            quietly at publish time three weeks later.
          </p>
        </Container>
      </section>

      {/* ───────── METHOD 3 — AI ───────── */}
      <section className='py-5 bg-light'>
        <Container fluid='lg'>
          <Row className='align-items-center g-5 flex-md-row-reverse'>
            <Col md={6}>
              <Image src={calendarShot} alt='The Greatly Social calendar showing posts scheduled across several networks after a bulk upload' fluid className='shadow-card rounded-3 border' />
            </Col>
            <Col md={6}>
              <p className='text-uppercase fw-bold text-primary small mb-2'>Method 3</p>
              <h2 className='fw-bold mb-3'>Let AI write the batch</h2>
              <p className='text-muted fs-5'>
                If you have the calendar but not the copy: describe your business and what
                you&rsquo;re promoting, choose how many posts you want, and AI writes the
                batch — captions tailored per network, with images generated or pulled from
                your own photos.
              </p>
              <p className='text-muted'>
                Every post lands in the same review screen. Rewrite any caption, regenerate
                any image, discard what you don&rsquo;t want, then schedule what&rsquo;s left.
              </p>
              <p className='text-muted mb-0'>
                Use it when you&rsquo;re starting from nothing. Use CSV when you already
                know exactly what you&rsquo;re posting.
              </p>
            </Col>
          </Row>
        </Container>
      </section>

      {/* ───────── PRICING ───────── */}
      <section className='py-5'>
        <Container fluid='lg' style={{ maxWidth: 860 }}>
          <h2 className='fw-bold mb-2'>Pricing</h2>
          <p className='text-muted fs-5 mb-4'>
            Bulk posting is included on every plan. Uploads draw from your monthly post
            allowance — a 40-row CSV uses 40 posts.
          </p>
          <div className='table-responsive mb-4'>
            <table className='table table-bordered align-middle text-center'>
              <thead className='table-light'>
                <tr><th className='text-start'>Plan</th><th>Price</th><th>Posts / month</th><th>Accounts</th></tr>
              </thead>
              <tbody>
                <tr><td className='text-start fw-semibold'>Starter</td><td>$9</td><td>15</td><td>3</td></tr>
                <tr><td className='text-start fw-semibold'>Pro</td><td>$29</td><td>30</td><td>10</td></tr>
                <tr><td className='text-start fw-semibold'>Agency</td><td>$69</td><td>100</td><td>20</td></tr>
              </tbody>
            </table>
          </div>
          <p className='text-muted mb-0'>
            Annual billing includes four months free. Every plan starts with a 14-day free
            trial, no card required. <a href='/plans'>Compare all plans →</a>
          </p>
        </Container>
      </section>

      {/* ───────── FAQ ───────── */}
      <section className='py-5 bg-light'>
        <Container style={{ maxWidth: 760 }}>
          <h2 className='fw-bold mb-4 text-center'>Bulk posting FAQs</h2>
          <Accordion flush defaultActiveKey='0'>
            {FAQS.map(([q, a], idx) => (
              <Accordion.Item eventKey={String(idx)} key={idx}>
                <Accordion.Header>{q}</Accordion.Header>
                <Accordion.Body className='text-muted'>{a}</Accordion.Body>
              </Accordion.Item>
            ))}
          </Accordion>
        </Container>
      </section>

      {/* ───────── CTA ───────── */}
      <section className='full-bleed py-5 text-center bg-primary text-white'>
        <div className='mx-auto px-4' style={{ maxWidth: 720 }}>
          <h2 className='fw-bold mb-3'>At Greatly Social you can fill next month in one upload</h2>
          <p className='lead mb-4'>
            Greatly Social queues a whole month from one spreadsheet. Start free for 14 days — no credit card.
          </p>
          <Button variant='light' size='lg' href='/register' className='me-2 mb-2'>
            Start free trial
          </Button>
          <Button variant='outline-light' size='lg' href={TEMPLATE} className='mb-2'>
            Download the CSV template
          </Button>
        </div>
      </section>

      {/* Benefit strip kept from v1 — it tested fine and adds a scannable summary */}
      <section className='py-5'>
        <Container fluid='lg'>
          <Row className='g-4'>
            {[
              ['Scale campaigns fast', 'Roll out a 30-day launch across eight networks in minutes.'],
              ['Reduce errors', 'One validation screen beats pasting the same post fifty times.'],
              ['Free your creative time', 'Let bulk mode handle the grunt work so you can focus on strategy.'],
            ].map(([title, body]) => (
              <Col md={4} key={title}>
                <p className='d-flex align-items-start gap-2 mb-2'>
                  <CheckCircleFill className='text-success mt-1 flex-shrink-0' />
                  <span><strong>{title}</strong><br /><span className='text-muted'>{body}</span></span>
                </p>
              </Col>
            ))}
          </Row>
        </Container>
      </section>
    </>
  )
}
